import ringArt from '../../assets/img/portal-ring.png';
import { prefersReducedMotion } from './portalMotion';

const wait = ms => new Promise(resolve => setTimeout(resolve, ms));
const GLYPHS = '█▓▒░#%&@/\\<>[]{}01Ø§¤≈';
const colours = ['#00FFFF', '#C6B8FF', '#EAB308', '#FFFFFF'];
const make = (tag, css, parent) => {
  const el = document.createElement(tag);
  el.style.cssText = css;
  parent.appendChild(el);
  return el;
};

/** The only live-page motion. Chrome and the overlay are siblings of the content. */
export function createDeparture(origin, { viewer = false, ringSize = 56, surface, doorway = false } = {}) {
  const reduced = prefersReducedMotion();
  const width = surface?.region.clientWidth || window.innerWidth;
  const height = surface?.region.clientHeight || window.innerHeight;
  const layer = make('div', `position:${surface ? 'absolute' : 'fixed'};inset:0;pointer-events:none;z-index:10000;overflow:hidden;`, surface?.region || document.body);
  layer.setAttribute('aria-hidden', 'true');
  layer.style.visibility = 'hidden';
  // `null`, never `false`: the viewer opens without a page surface, and `?.` on `false` still throws.
  const content = surface?.live || (!viewer ? document.querySelector('[data-pr-content]') : null);
  const top = surface ? 0 : document.querySelector('[data-pr-chrome]')?.getBoundingClientRect().bottom || 0;
  const bottom = surface ? height : document.querySelector('[data-pr-tabbar]')?.getBoundingClientRect().top || height;
  const dim = make('div', `position:absolute;left:0;right:0;top:${top}px;bottom:${height - bottom}px;background:#000;opacity:0;`, layer);
  const frames = make('canvas', `position:absolute;left:0;top:${top}px;width:100%;height:${bottom - top}px;`, layer);
  frames.width = width; frames.height = Math.max(1, bottom - top);
  const ring = make('div', `position:absolute;left:${origin.x - ringSize / 2}px;top:${origin.y - ringSize / 2}px;width:${ringSize}px;height:${ringSize}px;`, layer);
  ring.dataset.prRing = '';
  const core = make('canvas', 'position:absolute;inset:6.25%;width:87.5%;height:87.5%;border-radius:50%;background:#05030C;', ring);
  core.width = core.height = ringSize * 0.875;
  const art = make('div', `position:absolute;inset:0;background:url("${ringArt}") center/contain no-repeat;`, ring);
  if (!reduced) art.style.animation = 'pr-loading-spin 900ms linear infinite';
  let started = 0, raf = 0, last = 0, lastScramble = 0, holding = false, disposed = false, tailTimer;
  let tilt, grow, shade;
  const labels = [];
  const stars = Array.from({ length: 28 }, () => ({ a: Math.random() * Math.PI * 2, d: Math.random() }));
  let frameCtx, coreCtx;
  const original = content ? { transform: content.style.transform, origin: content.style.transformOrigin } : null;
  const restoreLabels = () => labels.splice(0).forEach(({ el, overlay, colour, position }) => { el.style.color = colour; el.style.position = position; overlay.remove(); });
  function draw(now) {
    if (disposed || !holding) return;
    raf = requestAnimationFrame(draw);
    if (document.hidden || now - last < 30) return;
    last = now;
    const t = now - started, intensity = Math.min(1, t / 1200);
    if (coreCtx) {
      const r = core.width / 2;
      coreCtx.clearRect(0, 0, core.width, core.height);
      stars.forEach((star, i) => {
        star.d += (0.012 + 0.03 * intensity) * (0.4 + 2.2 * star.d);
        if (star.d > 1.05) { star.d = 0.02; star.a = Math.random() * Math.PI * 2; }
        const len = 0.04 + star.d * (0.12 + 0.25 * intensity);
        coreCtx.beginPath();
        coreCtx.moveTo(r + Math.cos(star.a) * star.d * r, r + Math.sin(star.a) * star.d * r);
        coreCtx.lineTo(r + Math.cos(star.a) * Math.max(0, star.d - len) * r, r + Math.sin(star.a) * Math.max(0, star.d - len) * r);
        coreCtx.lineWidth = 0.6 + 1.4 * star.d; coreCtx.lineCap = 'round';
        coreCtx.globalAlpha = Math.min(1, 1.4 * star.d); coreCtx.strokeStyle = i % 5 ? '#FFFFFF' : '#00FFFF'; coreCtx.stroke();
      });
    }
    if (frameCtx && !viewer) {
      const w = frames.width, h = frames.height, y = origin.y - top;
      frameCtx.clearRect(0, 0, w, h);
      for (let i = 0; i < 7; i++) {
        const f = (i / 7 + t / 3300 * (0.35 + 0.8 * intensity)) % 1, e = f ** 1.6;
        frameCtx.strokeStyle = colours[(i + Math.floor(t / 670)) % 4];
        frameCtx.globalAlpha = (1 - f) * (0.25 + 0.5 * intensity);
        frameCtx.lineWidth = 1 + 2 * (1 - f);
        frameCtx.strokeRect(origin.x * e, y * e, w * (1 - e), h * (1 - e));
      }
    }
    if (now - lastScramble >= 40) {
      lastScramble = now;
      labels.forEach(({ overlay, text }) => {
        overlay.textContent = [...text].map(c => /\s/.test(c) || Math.random() > 0.12 + 0.7 * intensity ? c : GLYPHS[Math.floor(Math.random() * GLYPHS.length)]).join('');
      });
    }
  }
  function restore() {
    holding = false; cancelAnimationFrame(raf);
    tilt?.cancel(); grow?.cancel(); shade?.cancel(); restoreLabels();
    if (content) { content.style.transform = original.transform; content.style.transformOrigin = original.origin; }
  }
  return {
    ring,
    start() {
      if (reduced) return;
      layer.style.visibility = 'visible';
      holding = true; started = performance.now();
      // These are presentation overlays; the original accessible text is never replaced.
      content?.querySelectorAll('[data-pr-scramble]').forEach(el => {
        if (el.children.length || el.textContent.length > 100) return;
        const style = getComputedStyle(el), text = el.textContent, position = el.style.position;
        if (style.position === 'static') el.style.position = 'relative';
        const overlay = make('span', `position:absolute;inset:0;pointer-events:none;overflow:hidden;font:inherit;line-height:inherit;letter-spacing:inherit;text-transform:inherit;color:${style.color};`, el);
        overlay.setAttribute('aria-hidden', 'true'); overlay.textContent = text;
        labels.push({ el, overlay, text, colour: el.style.color, position }); el.style.color = 'transparent';
      });
      if (content) {
        const rect = content.getBoundingClientRect();
        content.style.transformOrigin = surface ? `${origin.x}px ${origin.y}px` : `${origin.x - rect.left}px ${origin.y - rect.top}px`;
        tilt = content.animate?.([
          { transform: 'none', offset: 0, easing: 'cubic-bezier(0,0,.2,1)' },
          { transform: 'perspective(900px) rotateX(4deg) rotateY(-3deg) rotateZ(-2deg) scale(.97)', offset: 260 / 1200, easing: 'cubic-bezier(.4,0,.6,1)' },
          { transform: 'perspective(900px) rotateX(16deg) rotateY(-12deg) rotateZ(-9deg) scale(.88)', offset: 1 },
        ], { duration: 1200, fill: 'forwards' });
        shade = dim.animate?.([{ opacity: 0 }, { opacity: 0.2 }], { duration: 260, fill: 'forwards' });
      }
      grow = ring.animate?.([
        { transform: `scale(${viewer || doorway ? 1 : 0.2})`, offset: 0, easing: 'cubic-bezier(0,0,.2,1)' },
        { transform: 'scale(1.25)', offset: 260 / 1200 }, { transform: 'scale(1.6)', offset: 1 },
      ], { duration: 1200, fill: 'forwards' });
      frameCtx = frames.getContext('2d'); coreCtx = core.getContext('2d');
      raf = requestAnimationFrame(draw);
    },
    snapshot(back = false) {
      layer.style.visibility = 'visible';
      // Freeze the actual held size. Never jump to the full 1.6 scale on a short hold.
      const box = ring.getBoundingClientRect();
      const size = holding ? box.width || ringSize : ringSize * (viewer || doorway ? 1 : 0.2);
      grow?.cancel();
      ring.style.cssText += `left:${origin.x - size / 2}px;top:${origin.y - size / 2}px;width:${size}px;height:${size}px;transform:none;view-transition-name:pr-ring;`;
      const s = document.documentElement.style;
      s.setProperty('--pr-ring-left', `${origin.x - size / 2}px`); s.setProperty('--pr-ring-top', `${origin.y - size / 2}px`);
      s.setProperty('--pr-hole', `${size / 2 * 0.875}px`);
      s.setProperty('--pr-core-left', `${origin.x - size / 2 * 0.875}px`);
      s.setProperty('--pr-core-top', `${origin.y - size / 2 * 0.875}px`);
      const cover = Math.hypot(Math.max(origin.x, width - origin.x), Math.max(origin.y, height - origin.y)) * 1.04;
      s.setProperty('--pr-cover', `${cover}px`); s.setProperty('--pr-ring-cover', cover / (size / 2 * 0.875));
      // Old root already captures the live tilt. Applying it again would double the departure.
      if (!holding) core.style.display = 'none';
      else if (!reduced) {
        core.style.viewTransitionName = 'pr-core';
        frames.style.viewTransitionName = 'pr-edges';
      }
      return back;
    },
    commit() {
      const keepTail = holding && !reduced;
      restore(); dim.remove();
      if (keepTail) {
        holding = true;
        raf = requestAnimationFrame(draw);
        tailTimer = setTimeout(() => {
          holding = false; cancelAnimationFrame(raf); frames.remove(); core.remove();
        }, 200);
      } else { frames.remove(); core.remove(); }
    },
    stop() { if (disposed) return; clearTimeout(tailTimer); restore(); disposed = true; layer.remove(); },
    async fail() {
      const transform = content && getComputedStyle(content).transform;
      const box = ring.getBoundingClientRect();
      restore();
      layer.style.visibility = 'visible';
      if (box.width) Object.assign(ring.style, { left: `${box.left}px`, top: `${box.top}px`, width: `${box.width}px`, height: `${box.height}px` });
      if (!reduced) {
        frames.animate?.([{ opacity: 1 }, { opacity: 0 }], { duration: 200, fill: 'forwards' });
        content?.animate?.([{ transform }, { transform: 'none' }], { duration: 300, easing: 'cubic-bezier(0,0,.2,1)' });
        ring.animate?.([0, -7, 7, -4, 4, 0].map(x => ({ transform: `translateX(${x}px)` })), { duration: 320 });
        await wait(320);
        if (disposed) return;
        ring.animate?.([{ transform: 'scale(1)' }, { transform: 'scale(0)' }], { duration: 240, easing: 'cubic-bezier(.4,0,1,1)', fill: 'forwards' });
        await wait(240);
      }
      this.stop();
    },
  };
}

export function showNavigationError() {
  document.querySelector('[data-pr-navigation-error]')?.remove();
  const toast = make('div', 'position:fixed;z-index:10001;bottom:calc(76px + env(safe-area-inset-bottom));left:50%;transform:translateX(-50%);max-width:calc(100vw - 32px);width:360px;background:#000;padding:16px 20px;font:11px monospace;color:#ffffffbf;', document.body);
  toast.dataset.prNavigationError = ''; toast.setAttribute('role', 'alert');
  const title = make('div', 'color:#EF4444;font-weight:700;letter-spacing:.1em;margin-bottom:6px;', toast);
  title.textContent = "COULDN'T OPEN THAT.";
  const message = make('div', '', toast); message.textContent = 'Check your connection and tap it again.';
  setTimeout(() => toast.remove(), 2400);
}
